/**
 * goBackOrHome — safe back navigation for deep-linkable screens.
 *
 * Problem it solves (#164):
 *   `navigation.goBack()` only works when there's a previous entry on
 *   the navigation stack. When a screen is reached by a COLD LOAD —
 *   a page refresh, or pasting a deep-link URL like `/scans`,
 *   `/notifications`, `/item/123` — React Navigation rebuilds the
 *   stack with ONLY that screen in it. `goBack()` then silently
 *   no-ops: the back button looks alive but does nothing, trapping the
 *   user on the page (reported on Scan History after refresh).
 *
 * The fix is the standard RN-Web pattern already used in
 * ShareClaimScreen + ForgotPasswordScreen: if we can pop, pop;
 * otherwise navigate to the Dashboard (Home tab) as a sane root.
 *
 * Usage:
 *   import { goBackOrHome } from '../../navigation/goBackOrHome';
 *   <Pressable onPress={() => goBackOrHome(navigation)} ... />
 *
 * `navigation` is typed loosely (any) because the call sites use
 * `useNavigation<any>()` — the helper only relies on the two methods
 * that exist on every navigator (`canGoBack`, `goBack`, `navigate`).
 */

export function goBackOrHome(navigation: any): void {
  if (navigation?.canGoBack?.()) {
    navigation.goBack();
    return;
  }
  // No history to pop (cold load / refresh / pasted deep link).
  // Land on the Dashboard tab inside the authenticated MainTabs stack.
  navigation?.navigate?.('MainTabs', { screen: 'Home' });
}

export default goBackOrHome;
